import { useEffect, useState } from 'react';
import { ImageOff } from '@/components/icons';
import { fetchArtifactBlob } from '@/lib/api';
import { artifactLabel } from '@/lib/artifacts';
import type { Artifact } from '@/lib/types';

/**
 * Artifacts need the login token, so they are fetched as blobs and shown through object URLs.
 * `enabled: false` defers the download (used for large videos and traces until the user asks).
 */
export function useArtifactUrl(resultId: string, name: string, publicToken?: string, enabled = true) {
  const [state, setState] = useState<{ url: string | null; blob: Blob | null; failed: boolean; loading: boolean }>({
    url: null,
    blob: null,
    failed: false,
    loading: false,
  });

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    setState((s) => ({ ...s, loading: true, failed: false }));
    fetchArtifactBlob(resultId, name, publicToken)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ url: objectUrl, blob, failed: false, loading: false });
      })
      .catch(() => !cancelled && setState({ url: null, blob: null, failed: true, loading: false }));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [resultId, name, publicToken, enabled]);

  return state;
}

/** Image thumbnail; clicking opens the full screenshot. */
export function ArtifactThumb({
  resultId,
  artifact,
  publicToken,
  onOpen,
}: {
  resultId: string;
  artifact: Artifact;
  publicToken?: string;
  onOpen: (url: string) => void;
}) {
  const { url, failed } = useArtifactUrl(resultId, artifact.name, publicToken);
  const label = artifactLabel(artifact.name);
  const tile = 'group relative block aspect-video w-40 overflow-hidden rounded-xl bg-slate-100 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700';

  if (failed) {
    return (
      <div className={`${tile} grid place-items-center text-slate-400`} title={`${label} could not be loaded`}>
        <ImageOff className="size-5" />
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`${tile} focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:outline-none`}
      disabled={!url}
      onClick={() => url && onOpen(url)}
      aria-label={`Open screenshot ${label}`}
      title={label}
    >
      {url ? (
        <img src={url} alt="" className="size-full object-cover transition duration-300 group-hover:scale-105" />
      ) : (
        <span className="block size-full animate-pulse bg-slate-200 dark:bg-slate-700" />
      )}
      <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/70 to-transparent px-2 pt-4 pb-1 text-left text-[11px] text-white">
        {label}
      </span>
    </button>
  );
}
